// ./src/utils/api/InventoryItemService.ts
//
// Inventory-item service — the account-level catalog of stock-tracked things
// (supplies, consumables, gear counted on hand). OpResult-native. Managed from the
// account Library page OR from an event's Inventory page: in the latter case a
// `contextEventId` AUTH CONTEXT lets crew with event.operate on that event manage
// the event account's catalog (the empty-picker fix — mirrors RentalAssetService).
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/inventory.ts)
// =============================================================================

export const INVENTORY_LOCATION_TIER = {
  HOME: "home",
  EVENT: "event",
} as const;

export const INVENTORY_LOCATION_TIER_VALUES = Object.values(INVENTORY_LOCATION_TIER);

export const INVENTORY_LOCATION_TIER_LABELS: Record<string, string> = {
  home: "Home",
  event: "Event",
};

/** A venue / event reference resolved live on read (current name). Null when removed. */
export interface InventoryRef {
  id?: string;
  name?: string | null;
}

/** A stock level at one lot/location — shared shape of onHandByLocation + inventoryBalances. */
export interface InventoryBalance {
  inventoryItemId?: string | null;
  /** The item, resolved live on the balance row (name/unit) — works for items off the loaded catalog page. */
  item?: Pick<InventoryItem, "id" | "name" | "unit"> | null;
  eventId?: string | null;
  event?: InventoryRef | null;
  venueId?: string | null;
  venue?: InventoryRef | null;
  venueAddressId?: string | null;
  expiresAt?: string | null;
  /** Derived: 'home' | 'event'. */
  tier?: string | null;
  onHand?: number | null;
  value?: number | null;
  /** Item-level alert: total on-hand across ALL locations is below the item's threshold. */
  lowStock?: boolean | null;
}

/** One scannable code attached to an item. */
export interface InventoryBarcode {
  id?: string;
  code?: string | null;
  label?: string | null;
  /** Stock units one scan of this code represents (a "case" barcode = 32). Default 1. */
  unitsPerCode?: number | null;
}

// =============================================================================
// GraphQL
// =============================================================================

const INVENTORY_BALANCE_SUBFIELDS = `
  inventoryItemId
  eventId
  event { id name }
  venueId
  venue { id name }
  venueAddressId
  expiresAt
  tier
  onHand
  value
`;

const INVENTORY_ITEM_FIELDS = `
  id
  accountId
  categoryId
  defaultVendorId
  defaultVendorLabel
  defaultVendor { id name }
  name
  description
  sku
  unit
  unitCost
  currencyCode
  lowStockThreshold
  trackLowStock
  minOrderQty
  lowStockNotifiedAt
  mainImageUploadedFileId
  notes
  barcodes { id code label unitsPerCode }
  totalOnHand
  value
  lowStock
  onHandByLocation { ${INVENTORY_BALANCE_SUBFIELDS} }
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const INVENTORY_ITEM_LIST_QUERY = gql`
  query InventoryItemList($filter: InventoryItemFilter, $params: PaginationAndSorting) {
    inventoryItemList(filter: $filter, params: $params) {
      code
      total
      data { ${INVENTORY_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const INVENTORY_ITEM_GET_QUERY = gql`
  query InventoryItemGet($id: ID) {
    inventoryItemGet(id: $id) {
      code
      data { ${INVENTORY_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const INVENTORY_ITEM_GET_MANY_QUERY = gql`
  query InventoryItemGetMany($ids: [ID]) {
    inventoryItemGetMany(ids: $ids) {
      code
      data { ${INVENTORY_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// One-tap scan-to-resolve: match a scanned/typed barcode to an item. Empty data → no match.
const INVENTORY_ITEM_BY_BARCODE_QUERY = gql`
  query InventoryItemByBarcode($code: String, $contextEventId: ID) {
    inventoryItemByBarcode(code: $code, contextEventId: $contextEventId) {
      code
      data { ${INVENTORY_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// Product-database lookup for an UNKNOWN scanned code — pre-fills a new item.
// Empty data = no match / feature disabled.
const INVENTORY_BARCODE_LOOKUP_QUERY = gql`
  query InventoryBarcodeLookup($code: String, $contextEventId: ID) {
    inventoryBarcodeLookup(code: $code, contextEventId: $contextEventId) {
      code
      data { code name brand category description imageUrl source }
      errors { name errors warnings }
    }
  }
`;

// A unique auto-generated SKU for the account.
const INVENTORY_ITEM_SUGGEST_SKU_QUERY = gql`
  query InventoryItemSuggestSku($contextEventId: ID) {
    inventoryItemSuggestSku(contextEventId: $contextEventId) {
      code
      data { sku }
      errors { name errors warnings }
    }
  }
`;

const INVENTORY_ITEM_CREATE_MUTATION = gql`
  mutation InventoryItemCreate($params: InventoryItemInput) {
    inventoryItemCreate(params: $params) {
      code
      data { ${INVENTORY_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const INVENTORY_ITEM_UPDATE_MUTATION = gql`
  mutation InventoryItemUpdate($where: InventoryItemWhereInput, $params: InventoryItemInput) {
    inventoryItemUpdate(where: $where, params: $params) {
      code
      data { ${INVENTORY_ITEM_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const INVENTORY_ITEM_REMOVE_MUTATION = gql`
  mutation InventoryItemRemove($where: InventoryItemWhereInput) {
    inventoryItemRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api InventoryItem schema)
// =============================================================================

export interface InventoryItem {
  id?: string;
  accountId?: string | null;
  categoryId?: string | null;
  /** Preferred supplier — soft ref → venue (kind='vendor'), for reordering. */
  defaultVendorId?: string | null;
  /** Display fallback name snapshot for the default vendor (when the venue was removed). */
  defaultVendorLabel?: string | null;
  /** The default vendor venue resolved live (current name); null if removed. */
  defaultVendor?: { id?: string | null; name?: string | null } | null;
  name?: string | null;
  description?: string | null;
  sku?: string | null;
  unit?: string | null;
  unitCost?: number | null;
  currencyCode?: string | null;
  lowStockThreshold?: number | null;
  trackLowStock?: boolean | null;
  /** Minimum quantity to buy when reordering (at/below the threshold); null = no minimum. */
  minOrderQty?: number | null;
  lowStockNotifiedAt?: string | null;
  mainImageUploadedFileId?: string | null;
  notes?: string | null;
  barcodes?: InventoryBarcode[] | null;
  /** DERIVED server-side: total quantity on hand across every lot/location. */
  totalOnHand?: number | null;
  /** DERIVED server-side: monetary value of the stock on hand. */
  value?: number | null;
  /** DERIVED server-side: at/under the low-stock threshold (only when tracked). */
  lowStock?: boolean | null;
  /** DERIVED server-side: the per-lot/location breakdown of on-hand stock. */
  onHandByLocation?: InventoryBalance[] | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface InventoryItemInput {
  /** Auth context (NOT stored): the event page a crew member is acting from. */
  contextEventId?: string | null;
  categoryId?: string | null;
  /** Preferred supplier — soft ref → venue (kind='vendor'). Send null to clear. */
  defaultVendorId?: string | null;
  name?: string | null;
  description?: string | null;
  sku?: string | null;
  unit?: string | null;
  unitCost?: number | null;
  currencyCode?: string | null;
  lowStockThreshold?: number | null;
  trackLowStock?: boolean | null;
  /** Minimum quantity to buy when reordering (at/below the threshold); null = no minimum. */
  minOrderQty?: number | null;
  mainImageUploadedFileId?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
  /** Full desired set, diffed on id server-side; omit to leave untouched. */
  barcodes?: InventoryBarcode[] | null;
}

export interface InventoryItemFilter {
  id?: string[] | null;
  categoryId?: string[] | null;
  status?: number[] | null;
  trackLowStock?: boolean | null;
  searchKeyword?: string | null;
  /** Auth/scoping context (NOT a column): the event page the list is shown on. */
  contextEventId?: string | null;
}

export interface InventoryItemWhereInput {
  id?: string | string[];
  contextEventId?: string | null;
}

/** A product-database hit for an unknown scanned code (free-text; NOT our category id). */
export interface InventoryBarcodeProduct {
  code?: string | null;
  name?: string | null;
  brand?: string | null;
  /** Free-text category from the product DB — a hint only, not a Category id. */
  category?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  source?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class InventoryItemService {
  static async list(
    filter?: InventoryItemFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(INVENTORY_ITEM_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Fetches EVERY item matching `filter` by paging through the list — for whole-set
   * consumers (export, the reorder scan) that must cover the full filtered set, not one page.
   */
  static async listAll(
    filter?: InventoryItemFilter,
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<InventoryItem>(InventoryItemService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(INVENTORY_ITEM_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(INVENTORY_ITEM_GET_MANY_QUERY, { ids });
  }

  /** Resolve a scanned/typed barcode to an item (account-scoped via contextEventId). */
  static async getByBarcode(code: string, contextEventId?: string | null): Promise<OpResult> {
    return ApiService.executeQuery(INVENTORY_ITEM_BY_BARCODE_QUERY, {
      code,
      contextEventId: contextEventId || undefined,
    });
  }

  /** Look an UNKNOWN code up in the product database to pre-fill a new item. Empty = no match. */
  static async lookupBarcode(code: string, contextEventId?: string | null): Promise<OpResult> {
    return ApiService.executeQuery(INVENTORY_BARCODE_LOOKUP_QUERY, {
      code,
      contextEventId: contextEventId || undefined,
    });
  }

  /** Ask the backend for a unique auto-generated SKU for the account. */
  static async suggestSku(contextEventId?: string | null): Promise<OpResult> {
    return ApiService.executeQuery(INVENTORY_ITEM_SUGGEST_SKU_QUERY, {
      contextEventId: contextEventId || undefined,
    });
  }

  static async create(params: InventoryItemInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_ITEM_CREATE_MUTATION, { params });
  }

  static async update(where: InventoryItemWhereInput, params: InventoryItemInput): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_ITEM_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string, contextEventId?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(INVENTORY_ITEM_REMOVE_MUTATION, {
      where: { id, contextEventId: contextEventId || undefined },
    });
  }

  /** Remove every selected item in one call. */
  static async removeMany(ids: string[], contextEventId?: string | null): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(INVENTORY_ITEM_REMOVE_MUTATION, {
      where: { id: ids, contextEventId: contextEventId || undefined },
    });
  }
}
